import { toCheckItem, toReplyDraft, type WireReplyDraft } from './mappers';
import { ReplyDraftError, type ReplyDraftErrorKind } from '../model/errors';

import type { components } from '@/types/api';
import { ApiError } from '@/util/fetchClient';

type WireErrorCode = NonNullable<components['schemas']['ReplyDraftError']['code']>;

const KIND_BY_CODE: Record<WireErrorCode, ReplyDraftErrorKind> = {
  stale_state: 'stale_state',
  must_fix_unacknowledged: 'must_fix',
  empty_reply: 'empty_reply',
  inquiry_closed: 'inquiry_closed',
  invalid_status: 'invalid_status',
  not_found: 'not_found',
  not_opted_in: 'not_opted_in',
  invalid_option: 'invalid',
  invalid_event: 'invalid',
};

type ErrorBody = {
  code?: unknown;
  details?: { current?: unknown; items?: unknown; composedHash?: unknown };
};

function readBody(body: unknown): ErrorBody {
  return typeof body === 'object' && body !== null ? (body as ErrorBody) : {};
}

/**
 * The kind a response names. Every reply-draft route sends a `code`; a
 * response without one never reached them, such as a gateway or auth
 * failure, and is read by its status alone.
 */
function kindOf(status: number, body: ErrorBody): ReplyDraftErrorKind {
  if (typeof body.code === 'string' && body.code in KIND_BY_CODE) {
    return KIND_BY_CODE[body.code as WireErrorCode];
  }

  return status === 404 ? 'not_found' : 'unknown';
}

/**
 * Maps anything a reply-draft request can throw to a `ReplyDraftError`. A
 * `TypeError` is what `fetch` rejects with when no response arrives, and an
 * `AbortError` is the app's own timeout.
 */
export function toReplyDraftError(error: unknown): ReplyDraftError {
  if (error instanceof ReplyDraftError) {
    return error;
  }

  if (error instanceof ApiError) {
    const body = readBody(error.body);

    const kind = kindOf(error.status, body);

    const current =
      kind === 'stale_state' && body.details?.current
        ? toReplyDraft(body.details.current as WireReplyDraft)
        : undefined;

    const mustFix =
      kind === 'must_fix' && Array.isArray(body.details?.items)
        ? {
            items: (body.details.items as Parameters<typeof toCheckItem>[0][]).map(toCheckItem),
            composedHash:
              typeof body.details.composedHash === 'string' ? body.details.composedHash : '',
          }
        : undefined;

    return new ReplyDraftError(kind, error.message, error.status, current, mustFix);
  }

  if (error instanceof Error && error.name === 'AbortError') {
    return new ReplyDraftError('timeout', error.message);
  }

  if (error instanceof TypeError) {
    return new ReplyDraftError('network', error.message);
  }

  return new ReplyDraftError('unknown', error instanceof Error ? error.message : 'Unknown error');
}
